import 'src/hooks/howlerConfig'
import { useEffect, useRef, useState } from 'react'
import { Howl } from 'howler'
import { getI18nSettings } from 'src/services/i18n'
import { playsVoiceOver } from 'src/services/i18n/utils/languageVariants'
import { ASSETS } from 'src/config'

interface SpriteData {
  src: string[]
  sprite: Record<string, [number, number]>
}

const howlCache = new Map<string, Howl>()

async function loadLocalizedNotification(language: string): Promise<Howl | null> {
  const jsonPath = `/assets/sounds/notifications/${language}/notifications.json?${ASSETS.NOTIFICATIONS_CACHE_BUSTER}`

  try {
    const res = await fetch(jsonPath)
    if (!res.ok) return null

    const data: SpriteData = await res.json()
    const howl = new Howl({
      src: data.src.map(
        (file) =>
          `/assets/sounds/notifications/${language}/${file}?${ASSETS.NOTIFICATIONS_CACHE_BUSTER}`
      ),
      autoplay: false,
      sprite: data.sprite,
      preload: true,
      volume: 1
    })

    howlCache.set(language, howl)
    return howl
  } catch (err) {
    return null
  }
}

export function useLocalizedNotification(language: string): Howl | null {
  const [howl, setHowl] = useState<Howl | null>(null)
  const prevLangRef = useRef<string | null>(null)

  // Get the default language
  const { fallbackLng } = getI18nSettings()

  useEffect(() => {
    let isMounted = true

    const load = async () => {
      const langToUse = language || fallbackLng

      // Unload previous
      if (prevLangRef.current && howlCache.has(prevLangRef.current)) {
        howlCache.get(prevLangRef.current)?.unload()
        howlCache.delete(prevLangRef.current)
      }

      // Voice-overs are recorded in English only; es-lotobola plays none instead of falling back to English.
      if (!playsVoiceOver(langToUse)) {
        prevLangRef.current = null
        if (isMounted) setHowl(null)
        return
      }

      const loadedHowl = howlCache.get(langToUse) ?? (await loadLocalizedNotification(langToUse))

      if (loadedHowl && isMounted) {
        setHowl(loadedHowl)
        prevLangRef.current = langToUse
        return
      }

      // Fallback to English if the original fails
      if (langToUse !== fallbackLng) {
        const fallback =
          howlCache.get(fallbackLng) ?? (await loadLocalizedNotification(fallbackLng))

        if (fallback && isMounted) {
          setHowl(fallback)
          prevLangRef.current = fallbackLng
        }
      }
    }

    void load()

    return () => {
      isMounted = false
    }
  }, [fallbackLng, language])

  return howl
}
